import React, { useMemo, useState } from 'react';
import { apiUrl } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './TripCalendar.css';

const localizer = dateFnsLocalizer({
  format, parse, startOfWeek, getDay, locales: { 'en-US': enUS },
});
const VIEWS = [Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA];

// Day/Week/Month calendar of the trip. Activities are timed events; lodging
// shows as multi-day all-day bands. Clicking an activity opens its detail page.
export default function TripCalendar({ tripId, activities, lodgings, destinations }) {
  const navigate = useNavigate();

  const events = useMemo(() => {
    const out = [];
    (Array.isArray(activities) ? activities : []).forEach(a => {
      if (!a.start_time) return;
      const start = new Date(a.start_time);
      if (Number.isNaN(start.getTime())) return;
      const endRaw = a.end_time ? new Date(a.end_time) : null;
      const end = endRaw && !Number.isNaN(endRaw.getTime()) ? endRaw : new Date(start.getTime() + 3600000);
      out.push({ id: a.activity_id, title: a.title || 'Activity', start, end, kind: 'activity' });
    });
    (Array.isArray(lodgings) ? lodgings : []).forEach(l => {
      if (!l.start_date) return;
      const start = new Date(`${String(l.start_date).slice(0, 10)}T00:00:00`);
      const end = new Date(`${String(l.end_date || l.start_date).slice(0, 10)}T23:59:59`);
      if (Number.isNaN(start.getTime())) return;
      out.push({ id: `lodge-${l.lodging_id}`, title: `🏨 ${l.lodging_name || 'Lodging'}`, start, end, allDay: true, kind: 'lodging' });
    });
    return out;
  }, [activities, lodgings]);

  const defaultDate = useMemo(() => {
    const firstEv = events.map(e => e.start).sort((a, b) => a - b)[0];
    if (firstEv) return firstEv;
    const firstDest = (Array.isArray(destinations) ? destinations : [])
      .map(d => d.start_date).filter(Boolean).sort()[0];
    return firstDest ? new Date(`${String(firstDest).slice(0, 10)}T00:00:00`) : new Date();
  }, [events, destinations]);

  const [view, setView] = useState(Views.MONTH);
  const [date, setDate] = useState(defaultDate);

  const eventPropGetter = (event) => ({
    style: event.kind === 'lodging'
      ? { backgroundColor: 'rgba(99,102,241,0.32)', borderColor: 'rgba(99,102,241,0.6)', color: '#fff' }
      : { backgroundColor: 'var(--tp-accent, #c8a96a)', borderColor: 'var(--tp-accent, #c8a96a)', color: '#fff' },
  });

  const onSelectEvent = (event) => {
    if (event.kind === 'activity') navigate(`/itinerary/${tripId}/activity/${event.id}`);
  };

  return (
    <section className="tp-section">
      <div className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <h2 className="tp-section__title" style={{ margin: 0 }}>Calendar</h2>
        <div className="tp-row" style={{ gap: 14 }}>
          <a className="tp-link" href={apiUrl(`/api/trips/${tripId}/calendar.ics`)} style={{ fontSize: 13 }}>⬇ Export .ics</a>
          <a className="tp-link" href={apiUrl(`/api/trips/${tripId}/itinerary.pdf`)} style={{ fontSize: 13 }}>⬇ Export PDF</a>
        </div>
      </div>
      <div className="tp-card tp-calendar-wrap" style={{ height: '74vh', padding: 12 }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          views={VIEWS}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          popup
          eventPropGetter={eventPropGetter}
          onSelectEvent={onSelectEvent}
          style={{ height: '100%' }}
        />
      </div>
    </section>
  );
}
